import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { FiBell, FiCheck, FiCopy, FiRotateCcw, FiSave, FiX } from 'react-icons/fi'
import './NotificationSettingsModal.css'

import { apiRequest, errorMessage } from './supportApi'

// ─── Types ────────────────────────────────────────────────────────────────────

export type NotifKind = 'team' | 'client'
type NotifPriority = 'Low' | 'Medium' | 'High' | 'Critical' | 'Emergency'
type NotifPreset = 'standard' | 'emergency'
type NotifAudience = 'assignee' | 'leader' | 'team'
type NotifChannel = 'email' | 'telegram' | 'teams'

interface NotifEventRule {
    audience: NotifAudience
    channels: NotifChannel[]
}

interface NotifPipelineRules {
    allow_member_assignment: boolean
    events: Record<string, NotifEventRule>
}

type NotificationSettings = Record<NotifPriority, NotifPipelineRules>

interface Payload {
    department: number
    department_name: string
    settings: NotificationSettings
    defaults: NotificationSettings
    presets: Record<NotifPreset, NotifPipelineRules>
}

interface DepartmentLike {
    id: number
    name: string
}

// ─── Per-kind configuration ───────────────────────────────────────────────────

const PRIORITIES: NotifPriority[] = ['Low', 'Medium', 'High', 'Critical', 'Emergency']

const PRIORITY_CLASS: Record<NotifPriority, string> = {
    Low: 'nsm-p-low',
    Medium: 'nsm-p-medium',
    High: 'nsm-p-high',
    Critical: 'nsm-p-critical',
    Emergency: 'nsm-p-emergency',
}

const CHANNELS: { key: NotifChannel; label: string }[] = [
    { key: 'email', label: 'Email' },
    { key: 'telegram', label: 'Telegram' },
    { key: 'teams', label: 'MS Teams' },
]

const AUDIENCE_LABEL: Record<NotifAudience, string> = {
    assignee: 'Assignee',
    leader: 'Team leader',
    team: 'Whole team',
}

const KIND_CONFIG: Record<NotifKind, {
    title: string
    audiences: NotifAudience[]
    events: { key: string; label: string; hint: string }[]
    presets: { key: NotifPreset; label: string; description: string }[]
}> = {
    team: {
        title: 'Team ticket notifications',
        audiences: ['leader', 'team'],
        events: [
            { key: 'submitted', label: 'New ticket', hint: 'Submitted, raised to this priority, or re-routed to this team' },
            { key: 'assigned', label: 'Assigned', hint: 'The assignee is always notified on these channels; the audience gets an FYI' },
            { key: 'in_progress', label: 'In Progress', hint: 'The submitter is always emailed as well' },
            { key: 'resolved', label: 'Resolved', hint: 'The submitter is always emailed, with resolution notes' },
            { key: 'closed', label: 'Closed', hint: 'Off by default' },
        ],
        presets: [
            { key: 'standard', label: 'Default pipeline', description: 'Only the team leader is notified, by email. Only the leader can assign.' },
            { key: 'emergency', label: 'Emergency pipeline', description: 'The whole team is notified by email and Telegram. Anyone on the team can assign.' },
        ],
    },
    client: {
        title: 'Client ticket notifications',
        audiences: ['assignee', 'leader', 'team'],
        events: [
            { key: 'submitted', label: 'Routed to the team', hint: 'The receiver sent a client ticket here, raised its priority, or re-routed it here' },
            { key: 'assigned', label: 'Assigned', hint: 'The assignee is always notified; pick who else gets an FYI' },
            { key: 'client_reply', label: 'Customer replied', hint: 'New chat message from the customer, including requested input' },
            { key: 'status_change', label: 'Status changed', hint: 'Investigating, In Progress, Implementing or Resolved' },
            { key: 'validation_result', label: 'Customer tested the fix', hint: 'The customer confirmed it, or said it isn’t fixed' },
            { key: 'reopened', label: 'Reopened', hint: 'The customer reopened a resolved ticket' },
        ],
        presets: [
            { key: 'standard', label: 'Default pipeline', description: 'The leader hears about new tickets; the assignee hears about replies. Email only. Only the leader can assign.' },
            { key: 'emergency', label: 'Emergency pipeline', description: 'The whole team is notified by email and Telegram. Anyone on the team can assign.' },
        ],
    },
}

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v))

function sameRules(a: NotifPipelineRules, b: NotifPipelineRules, eventKeys: string[]): boolean {
    if (a.allow_member_assignment !== b.allow_member_assignment) return false
    return eventKeys.every(key => {
        const x = a.events[key], y = b.events[key]
        if (!x || !y) return false
        return x.audience === y.audience && [...x.channels].sort().join() === [...y.channels].sort().join()
    })
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function NotificationSettingsModal({
    kind,
    departments,
    onClose,
}: {
    kind: NotifKind
    departments: DepartmentLike[]     // only the ones this user may manage
    onClose: () => void
}) {
    const config = KIND_CONFIG[kind]
    const eventKeys = config.events.map(e => e.key)
    const basePath = (id: number) => `/portal/api/support/departments/${id}/notifications/?kind=${kind}`

    const [deptId, setDeptId] = useState<number | null>(departments[0]?.id ?? null)
    const [priority, setPriority] = useState<NotifPriority>('Low')
    const [settings, setSettings] = useState<NotificationSettings | null>(null)
    const [defaults, setDefaults] = useState<NotificationSettings | null>(null)
    const [presets, setPresets] = useState<Record<NotifPreset, NotifPipelineRules> | null>(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [dirty, setDirty] = useState(false)
    const [copySource, setCopySource] = useState<NotifPriority | ''>('')

    const applyPayload = (data: Payload) => {
        setSettings(data.settings)
        setDefaults(data.defaults)
        setPresets(data.presets)
        setDirty(false)
    }

    useEffect(() => { setCopySource('') }, [priority])

    useEffect(() => {
        if (deptId == null) return
        let cancelled = false
        setLoading(true)
        apiRequest<Payload>(basePath(deptId)).then(({ ok, data }) => {
            if (cancelled) return
            if (ok) applyPayload(data)
            else {
                setSettings(null)
                toast.error(errorMessage(data, 'Could not load notification settings.'))
            }
            setLoading(false)
        })
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deptId, kind])

    const confirmDiscard = () => !dirty || window.confirm('Discard unsaved changes?')
    const handleClose = () => { if (confirmDiscard()) onClose() }

    const changeDepartment = (id: number) => {
        if (id === deptId || !confirmDiscard()) return
        setDeptId(id)
    }

    const updateRules = (fn: (r: NotifPipelineRules) => NotifPipelineRules) => {
        setSettings(prev => prev && { ...prev, [priority]: fn(prev[priority]) })
        setDirty(true)
    }

    const updateEvent = (event: string, patch: Partial<NotifEventRule>) => {
        updateRules(r => ({ ...r, events: { ...r.events, [event]: { ...r.events[event], ...patch } } }))
    }

    const toggleChannel = (event: string, channel: NotifChannel) => {
        if (!settings) return
        const current = settings[priority].events[event].channels
        const next = current.includes(channel) ? current.filter(c => c !== channel) : [...current, channel]
        updateEvent(event, { channels: CHANNELS.map(c => c.key).filter(k => next.includes(k)) })
    }

    const applyPreset = (preset: NotifPreset) => { if (presets) updateRules(() => clone(presets[preset])) }
    const copyFrom = (source: NotifPriority) => { if (settings) updateRules(() => clone(settings[source])) }
    const resetThisPriority = () => { if (defaults) updateRules(() => clone(defaults[priority])) }

    const applyToAllStandard = () => {
        if (!settings) return
        const source = settings[priority]
        setSettings(prev => {
            if (!prev) return prev
            const next = { ...prev }
            PRIORITIES.filter(p => p !== 'Emergency').forEach(p => { next[p] = clone(source) })
            return next
        })
        setDirty(true)
    }

    const handleSave = async () => {
        if (!settings || deptId == null) return
        setSaving(true)
        const { ok, data } = await apiRequest<Payload>(basePath(deptId), { method: 'PATCH', json: settings })
        setSaving(false)
        if (!ok) { toast.error(errorMessage(data, 'Settings not saved. Try again.')); return }
        applyPayload(data)
        toast.success('Notification settings saved.')
    }

    const handleResetAll = async () => {
        if (deptId == null || !window.confirm('Reset every priority for this team to the defaults?')) return
        setSaving(true)
        const { ok, data } = await apiRequest<Payload>(basePath(deptId), { method: 'DELETE' })
        setSaving(false)
        if (!ok) { toast.error(errorMessage(data, 'Settings not reset. Try again.')); return }
        applyPayload(data)
        toast.success('Reset to defaults.')
    }

    const rules = settings?.[priority]
    const isCustom = (p: NotifPriority) => !!settings && !!defaults && !sameRules(settings[p], defaults[p], eventKeys)
    const activePreset: NotifPreset | null =
        rules && presets ? config.presets.find(pr => sameRules(rules, presets[pr.key], eventKeys))?.key ?? null : null

    return (
        <div className="nsm nsm-overlay" onClick={handleClose}>
            <div className="nsm-box" role="dialog" aria-modal="true" aria-label={config.title} onClick={e => e.stopPropagation()}>
                <div className="nsm-header">
                    <h3><FiBell size={14} /> {config.title}</h3>
                    <button className="nsm-close" aria-label="Close" onClick={handleClose}><FiX /></button>
                </div>

                <div className="nsm-body">
                    {departments.length > 1 && (
                        <label className="nsm-field">
                            <span>Team</span>
                            <select className="nsm-select" value={deptId ?? ''} onChange={e => changeDepartment(Number(e.target.value))}>
                                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                        </label>
                    )}

                    <div className="nsm-priority-tabs" role="tablist">
                        {PRIORITIES.map(p => (
                            <button
                                key={p}
                                role="tab"
                                aria-selected={priority === p}
                                className={`nsm-priority-tab ${priority === p ? 'active' : ''} ${p === 'Emergency' ? 'is-emergency' : ''}`}
                                onClick={() => setPriority(p)}
                            >
                                <span className={`nsm-badge ${PRIORITY_CLASS[p]}`}>{p}</span>
                                {isCustom(p) && <span className="nsm-custom-dot" title="Changed from the default" />}
                            </button>
                        ))}
                    </div>

                    {loading && <div className="nsm-loading">Loading settings…</div>}

                    {!loading && rules && (
                        <>
                            <section className="nsm-section">
                                <div className="nsm-section-head">
                                    <div>
                                        <h4>Choose a premade pipeline</h4>
                                        <p>Sets every rule below for <strong>{priority}</strong> tickets in one click. You can still fine-tune individual events afterwards.</p>
                                    </div>
                                    {!activePreset && <span className="nsm-custom-badge">Custom</span>}
                                </div>
                                <div className="nsm-preset-grid">
                                    {config.presets.map(pr => {
                                        const active = activePreset === pr.key
                                        return (
                                            <button key={pr.key} type="button" aria-pressed={active}
                                                className={`nsm-preset ${active ? 'active' : ''} ${pr.key === 'emergency' ? 'is-emergency' : ''}`}
                                                onClick={() => applyPreset(pr.key)}>
                                                <span className="nsm-preset-check">{active && <FiCheck size={11} />}</span>
                                                <span className="nsm-preset-body">
                                                    <strong>{pr.label}</strong>
                                                    <span>{pr.description}</span>
                                                </span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </section>

                            <button type="button" aria-pressed={rules.allow_member_assignment}
                                className={`nsm-toggle ${rules.allow_member_assignment ? 'on' : ''}`}
                                onClick={() => updateRules(r => ({ ...r, allow_member_assignment: !r.allow_member_assignment }))}>
                                <span className="nsm-switch"><span className="nsm-switch-knob" /></span>
                                <span className="nsm-toggle-text">
                                    <strong>Any team member can assign {priority} tickets</strong>
                                    <span>{rules.allow_member_assignment
                                        ? 'Team members can claim a ticket or assign it to anyone.'
                                        : 'Only the team leader (or a manager) can assign.'}</span>
                                </span>
                            </button>

                            <div className="nsm-grid">
                                <div className="nsm-grid-head">
                                    <span>Event</span><span>Notify</span><span>Channels</span>
                                </div>
                                {config.events.map(ev => {
                                    const rule = rules.events[ev.key]
                                    if (!rule) return null
                                    const off = rule.channels.length === 0
                                    return (
                                        <div key={ev.key} className={`nsm-row ${off ? 'is-off' : ''}`}>
                                            <div className="nsm-event">
                                                <strong>{ev.label}</strong>
                                                <span>{ev.hint}</span>
                                            </div>
                                            <div className="nsm-segment" role="group" aria-label={`Who to notify: ${ev.label}`}>
                                                {config.audiences.map(a => (
                                                    <button key={a} className={rule.audience === a ? 'active' : ''}
                                                        aria-pressed={rule.audience === a}
                                                        onClick={() => updateEvent(ev.key, { audience: a })}>
                                                        {a === 'assignee' && ev.key === 'assigned' ? 'Assignee only' : AUDIENCE_LABEL[a]}
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="nsm-channels">
                                                {CHANNELS.map(ch => (
                                                    <button key={ch.key}
                                                        className={`nsm-chip ${rule.channels.includes(ch.key) ? 'active' : ''}`}
                                                        aria-pressed={rule.channels.includes(ch.key)}
                                                        onClick={() => toggleChannel(ev.key, ch.key)}>
                                                        {ch.label}
                                                    </button>
                                                ))}
                                                {off && <span className="nsm-off">Off</span>}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>

                            <section className="nsm-section nsm-tools">
                                <div className="nsm-section-head">
                                    <div>
                                        <h4>Reuse these settings</h4>
                                        <p>Copy rules between priorities instead of setting each one up by hand.</p>
                                    </div>
                                </div>

                                <div className="nsm-tool">
                                    <div className="nsm-tool-text">
                                        <strong>Copy from another priority</strong>
                                        <span>Replace the {priority} rules with the ones from the priority you pick.</span>
                                    </div>
                                    <div className="nsm-tool-actions">
                                        <select className="nsm-select nsm-select-sm" value={copySource}
                                            onChange={e => setCopySource(e.target.value as NotifPriority | '')}>
                                            <option value="">Select priority…</option>
                                            {PRIORITIES.filter(p => p !== priority).map(p => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                        <button type="button" className="nsm-btn" disabled={!copySource}
                                            onClick={() => { if (copySource) { copyFrom(copySource); setCopySource('') } }}>
                                            <FiCopy size={12} /> Copy
                                        </button>
                                    </div>
                                </div>

                                {priority !== 'Emergency' && (
                                    <div className="nsm-tool">
                                        <div className="nsm-tool-text">
                                            <strong>Apply to all standard priorities</strong>
                                            <span>Use these {priority} rules for Low, Medium, High and Critical tickets. Emergency is left unchanged.</span>
                                        </div>
                                        <div className="nsm-tool-actions">
                                            <button type="button" className="nsm-btn" onClick={applyToAllStandard}>Apply to Low–Critical</button>
                                        </div>
                                    </div>
                                )}

                                {isCustom(priority) && (
                                    <div className="nsm-tool">
                                        <div className="nsm-tool-text">
                                            <strong>Reset {priority} to default</strong>
                                            <span>Undo changes to this priority only. Other priorities are kept.</span>
                                        </div>
                                        <div className="nsm-tool-actions">
                                            <button type="button" className="nsm-btn" onClick={resetThisPriority}>
                                                <FiRotateCcw size={12} /> Reset
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </section>

                            <p className="nsm-footnote">
                                Rules follow the ticket&apos;s priority when each event happens. The person who makes a change is
                                never notified about it.{kind === 'client' && ' Customers are always emailed about their own tickets; these settings only control FNN notifications.'}
                            </p>
                        </>
                    )}
                </div>

                <div className="nsm-footer">
                    <button className="nsm-btn" onClick={handleResetAll} disabled={saving || loading}>
                        <FiRotateCcw size={12} /> Reset all to defaults
                    </button>
                    <div className="nsm-footer-right">
                        <button className="nsm-btn" onClick={handleClose}>Close</button>
                        <button className="nsm-btn nsm-btn-primary" onClick={handleSave} disabled={saving || loading || !dirty}>
                            <FiSave size={13} /> {saving ? 'Saving…' : 'Save changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}